<!--
  The cross-project scheduling queue. Three groups, ordered by who owes the next move; every row's
  label, group and pending action is derived from the ported transition table, never listed here.

  🔴 The row's inline action does NOT perform the transition. It emits, and this page opens the row,
  which fetches the visit's detail and hands it to <site-visit-negotiation> — the same component the
  per-project area uses, which already carries every state's interaction. Nothing here re-implements
  one: two surfaces performing the same transitions is exactly where a silent divergence starts.

  The row itself is <app-scheduling-queue-row>, shared with the workspace's site-visit card, for the
  same reason. This page owns the queue read, the open row and its detail; the row owns how a visit
  reads.
-->
<div class="list-page list-page--wide">
  <header class="page-header">
    <h1 class="page-header__title">Scheduling</h1>
    <p class="page-header__subtitle">
      Every site visit you have in flight, across all your projects — grouped by who
      owes the next move.
    </p>
  </header>

  @if (loading) {
  <div class="empty-state" data-testid="scheduling-loading">
    <p class="empty-state__title">Loading site visits…</p>
  </div>
  } @else if (loadFailed) {
  <div class="empty-state" data-testid="scheduling-error">
    <p class="empty-state__title">Your site visits couldn't be loaded</p>
    <p class="empty-state__text">
      Something went wrong on our side. Reload the page to try again.
    </p>
  </div>
  } @else if (!hasRows) {
  <!-- No visits at all — deliberately a different message from "nothing awaiting you". -->
  <div class="empty-state" data-testid="scheduling-empty">
    <p class="empty-state__title">No site visits scheduled</p>
    <p class="empty-state__text">
      Site visits appear here once a project you're working needs one. Completed and
      cancelled visits aren't listed.
    </p>
  </div>
  } @else { @for (group of groups; track trackByGroupKey($index, group)) {
  <!--
    Empty groups are hidden, with one exception: *Awaiting you* always renders. An empty first group
    with rows below it is the good outcome, and saying so is worth more than the row of space it costs.
  -->
  @if (group.key === 'AWAITING_YOU' || group.rows.length > 0) {
  <section
    class="queue-group"
    [class.queue-group--primary]="group.key === 'AWAITING_YOU'"
    [attr.data-testid]="group.testId"
  >
    <header class="queue-group__header">
      <h2 class="queue-group__title">
        {{ group.title }}
        <span class="queue-group__count" [attr.data-testid]="group.testId + '-count'">
          {{ group.count }}
        </span>
      </h2>
      <p class="queue-group__hint">{{ group.hint }}</p>
    </header>

    <!--
      This renders whenever nothing is awaiting the PA — including when the group still holds rows the PA
      settled in this session, which keep their place deliberately. So the copy says only that nothing is
      waiting on them, and scopes its account of where things stand to what is still in play; a settled
      visit is neither with someone else nor confirmed, and the earlier wording claimed it was.
    -->
    @if (group.key === 'AWAITING_YOU' && nothingAwaitingYou) {
    <div class="empty-state empty-state--inline" data-testid="scheduling-empty-awaiting">
      <p class="empty-state__title">Nothing needs you right now</p>
      <p class="empty-state__text">
        No site visit is waiting on your next move. Anything still in play is with the site
        contact, the project manager, or already confirmed.
      </p>
    </div>
    }

    @for (vm of group.rows; track trackByVisitId($index, vm)) {
    <!--
      The row itself — including its `id`, the scroll anchor for the `?visitId=` deep link the associate
      notification email lands on. The anchor is keyed by visit id, not by the flat-composite testid,
      because the email knows the visit and nothing else about where the row ends up; it is bound inside
      the component from {@link siteVisitAnchorId}, the same function `applyDeepLink` scrolls to.

      The pending action and *View Details* are wired to the same handler, which is the component's
      contract rather than an oversight: from the row, both do the identical thing — they open the row —
      and the choice between pending moves is made one click in, in the negotiation panel.

      🔴 *Reschedule* is the one control that is NOT the same handler, and the extra line is the fix for
      the second click it used to cost. It emits its own output so this page can record WHY the row
      opened; `openForReschedule` opens it exactly as `open` does and additionally sets
      `rescheduleIntent`, which is bound into the panel below. Wiring it back to `open(vm)` to tidy the
      template away would put the reschedule form back behind its disclosure.
    -->
    <app-scheduling-queue-row
      [vm]="vm"
      [open]="isOpen(vm)"
      (action)="open(vm)"
      (toggle)="open(vm)"
      (reschedule)="openForReschedule(vm)"
    >
      <!--
        The opened row's detail, projected in. It stays on the page because the page owns the fetch: the
        detail read is what heals a visit parked awaiting a `SYSTEM` advance, and a presentational row
        owns no data. 🔴 The `@if` is here, not in the component: projected content is built in the view
        that declares it, so guarding inside the row instead would instantiate a
        <site-visit-negotiation> — and its detail bindings — for every row in the queue.
      -->
      @if (isOpen(vm)) {
      <div class="queue-row__detail" [attr.data-testid]="vm.testId + '-detail'">
        @if (detailLoading) {
        <p class="queue-row__detail-note" [attr.data-testid]="vm.testId + '-detail-loading'">
          Loading this site visit…
        </p>
        } @else if (detailFailed) {
        <p class="queue-row__detail-note" [attr.data-testid]="vm.testId + '-detail-error'">
          This site visit couldn't be loaded. Open it from the project instead.
        </p>
        } @else if (detail) {
        <!-- Every state's interaction, unchanged, from the component the project view already uses. -->
        <!--
          `startRescheduleExpanded` is read ONCE, in the panel's `ngOnInit`. The panel is rebuilt on
          every open (this `@if`), so the binding is what each open sees and nothing carries over.
        -->
        <site-visit-negotiation
          [visit]="detail"
          [identityInHost]="true"
          [startRescheduleExpanded]="rescheduleIntent"
          (visitChange)="onVisitChange($event)"
        ></site-visit-negotiation>
        }

        <!-- Deep link out for anything the queue does not handle inline. -->
        @if (vm.row.projectId) {
        <a
          class="queue-row__project-link"
          [routerLink]="['/project-overview', vm.row.projectId]"
          [attr.data-testid]="vm.testId + '-project-link'"
        >
          <span class="material-icons" aria-hidden="true">open_in_new</span>
          Open the project
        </a>
        }
      </div>
      }
    </app-scheduling-queue-row>
    }
  </section>
  } } }
</div>
